import React, { useCallback, useRef, useState } from 'react';

export interface ReferralFilterState {
  enteredVia: string; // '' | 'signup' | 'settings'
  status: string; // '' | 'pending' | 'counted' | 'checkin_verified'
  channel: string; // free text (V159) — never a hardcoded enum
  code: string;
  q: string;
  /** True = only referrals whose referred user has zero visits. A real fact
   *  about a present user — distinct from the "account deleted" rows, which
   *  this filter neither includes nor excludes on its own. */
  neverVisited: boolean;
}

export const EMPTY_REFERRAL_FILTERS: ReferralFilterState = {
  enteredVia: '',
  status: '',
  channel: '',
  code: '',
  q: '',
  neverVisited: false,
};

export interface ReferralFiltersProps {
  value: ReferralFilterState;
  /** Called with an UPDATER that merges this control's change into whatever
   *  the parent's filter state is AT COMMIT TIME — never with a value built
   *  from the `value` prop captured earlier. The Code and Search boxes are
   *  debounced by 400ms, so a commit built from the captured `value` would
   *  overwrite anything the immediate controls (Never visited, Status,
   *  Channel, Entry point) changed in the meantime: tick "Never visited"
   *  within 400ms of typing in Code and it would visibly untick itself when
   *  the Code timer fired.
   *
   *  Every call resets paging to page 0 — the caller must not skip that step,
   *  or a narrower result set can leave the page stranded past its own last
   *  page. */
  onChange: (update: (prev: ReferralFilterState) => ReferralFilterState) => void;
  /** Known channel values seen in the data, offered as a datalist so the free
   *  text input still gets autocomplete without hiding channels the list
   *  doesn't know about yet (channel is free-form by design — migration V159). */
  knownChannels: string[];
}

// `entered_via` accepts only these two values — reused by ReferralList to
// label the "entry" column so the two components never drift.
export const ENTERED_VIA_LABELS: Record<string, string> = {
  signup: 'Signup',
  settings: 'Settings',
};

// The backend accepts exactly these three status values and 400s on anything
// else, so the control offers exactly these rather than free text. Reused by
// ReferralList to label the "status" column.
export const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  counted: 'Counted',
  checkin_verified: 'Check-in verified',
};

const ENTERED_VIA_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All entry points' },
  { value: 'signup', label: ENTERED_VIA_LABELS.signup },
  { value: 'settings', label: ENTERED_VIA_LABELS.settings },
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: STATUS_LABELS.pending },
  { value: 'counted', label: STATUS_LABELS.counted },
  { value: 'checkin_verified', label: STATUS_LABELS.checkin_verified },
];

const selectClass =
  'px-3 py-1.5 bg-gray-950 border border-gray-700 rounded-lg text-xs text-gray-200 focus:outline-none focus:border-violet-500';
const inputClass =
  'px-3 py-1.5 bg-gray-950 border border-gray-700 rounded-lg text-xs text-gray-200 placeholder-gray-600 focus:outline-none focus:border-violet-500';

/**
 * Filter controls for the referrals list: entry point and status are fixed
 * enums the backend validates and 400s outside of (so they're `<select>`s,
 * not text inputs); channel is free-form by design (migration V159) and gets
 * a text input backed by a datalist of channels already seen in the data,
 * rather than a hardcoded list that would hide new channels; code and `q`
 * are plain search text.
 *
 * Every control change calls `onChange` with an updater that merges its own
 * field into the latest filter state — `ReferralList` is responsible for
 * resetting `offset` to 0 on every call, matching how `UserTable` resets
 * `page` on every search/sort change.
 */
export function ReferralFilters({ value, onChange, knownChannels }: ReferralFiltersProps): React.ReactElement {
  const [codeInput, setCodeInput] = useState(value.code);
  const [qInput, setQInput] = useState(value.q);
  // Separate timers for the Code box and the Search box. They used to share
  // one timer: typing in Code then typing in Search before the first 400ms
  // elapsed cleared the pending Code commit and replaced it with a Search-
  // only onChange built from the (stale) `value.code` — the Code box kept
  // showing what the user typed, but it was never applied. Each input must
  // own its debounce so what is displayed always matches what is filtered.
  const codeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const qDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Every handler commits an UPDATER, so the change is merged into the LATEST
  // filter state rather than into the `value` this render closed over. That
  // matters most for the two debounced boxes below, whose commit fires up to
  // 400ms after the keystroke that scheduled it — long enough for the user to
  // have ticked "Never visited" or changed Status in between. `useCallback`
  // recreating a handler does NOT rescue an already-scheduled timer, so the
  // timer itself has to be immune to staleness.
  const handleEnteredVia = useCallback(
    (v: string) => onChange((prev) => ({ ...prev, enteredVia: v })),
    [onChange],
  );
  const handleStatus = useCallback(
    (v: string) => onChange((prev) => ({ ...prev, status: v })),
    [onChange],
  );
  const handleChannel = useCallback(
    (v: string) => onChange((prev) => ({ ...prev, channel: v })),
    [onChange],
  );
  const handleNeverVisited = useCallback(
    (v: boolean) => onChange((prev) => ({ ...prev, neverVisited: v })),
    [onChange],
  );
  const handleCode = useCallback(
    (v: string) => {
      setCodeInput(v);
      if (codeDebounceRef.current) clearTimeout(codeDebounceRef.current);
      codeDebounceRef.current = setTimeout(() => {
        onChange((prev) => ({ ...prev, code: v }));
      }, 400);
    },
    [onChange],
  );
  const handleQ = useCallback(
    (v: string) => {
      setQInput(v);
      if (qDebounceRef.current) clearTimeout(qDebounceRef.current);
      qDebounceRef.current = setTimeout(() => {
        onChange((prev) => ({ ...prev, q: v }));
      }, 400);
    },
    [onChange],
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label="Entry point"
        value={value.enteredVia}
        onChange={(e) => handleEnteredVia(e.target.value)}
        className={selectClass}
      >
        {ENTERED_VIA_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      <select
        aria-label="Status"
        value={value.status}
        onChange={(e) => handleStatus(e.target.value)}
        className={selectClass}
      >
        {STATUS_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      <input
        type="text"
        list="referral-known-channels"
        value={value.channel}
        onChange={(e) => handleChannel(e.target.value)}
        placeholder="Channel…"
        aria-label="Channel"
        className={`${inputClass} w-32`}
      />
      <datalist id="referral-known-channels">
        {knownChannels.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>

      <input
        type="text"
        value={codeInput}
        onChange={(e) => handleCode(e.target.value)}
        placeholder="Code…"
        aria-label="Code"
        className={`${inputClass} w-28`}
      />

      <label className="flex items-center gap-1.5 px-1 text-xs text-gray-300 select-none cursor-pointer whitespace-nowrap">
        <input
          type="checkbox"
          checked={value.neverVisited}
          onChange={(e) => handleNeverVisited(e.target.checked)}
          aria-label="Never visited"
          className="accent-violet-500"
        />
        Never visited
      </label>

      <div className="relative w-full sm:w-64">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="text"
          value={qInput}
          onChange={(e) => handleQ(e.target.value)}
          placeholder="Search referrer or referred…"
          aria-label="Search referrals"
          className={`${inputClass} w-full pl-9 pr-3`}
        />
      </div>
    </div>
  );
}

export default ReferralFilters;
